import React, { useState, useEffect } from "react";
import { Container, Button, Spinner, Alert } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import useAuthStore from "../stores/authStore";
import { FaTools, FaRunning } from "react-icons/fa";

import { Viewer } from "@toast-ui/react-editor";
import "@toast-ui/editor/dist/toastui-editor-viewer.css";
import CommentSection from "../components/CommentSection";

const DiaryDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const isAdmin = user?.role === 'admin';

    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const response = await axiosInstance.get(`postDetail?post_id=${id}`);
                const postData = response.data.data.postList[0]?.json;
                if (postData) {
                    setPost(postData);
                } else {
                    setError("게시글을 찾을 수 없습니다.");
                }
            } catch (err) {
                console.error(err);
                setError("게시글 불러오기 실패");
            } finally {
                setLoading(false);
            }
        };
        fetchPost();
    }, [id]);

    // 배경색 고정 (사용자 요청)
    const cardStyle = {
        backgroundColor: '#fff9eb' // Lighter Soft Orange/Cream
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" />
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-5 text-center">
                <Alert variant="danger">{error}</Alert>
                <Button variant="secondary" onClick={() => navigate("/")}>목록으로</Button>
            </Container>
        );
    }

    return (
        <Container className="py-5" style={{ maxWidth: '800px' }}>
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Gamja+Flower&family=Noto+Sans+KR:wght@300;400;500;700&display=swap');

                    .post-it-detail {
                        border: none;
                        border-radius: 2px;
                        padding: 40px;
                        box-shadow: 5px 5px 15px rgba(0,0,0,0.2);
                        position: relative;
                        min-height: 600px;
                        font-family: 'Noto Sans KR', sans-serif;
                    }

                    /* 테이프 네비게이션 (공통) */
                    .tape-nav, .tape-bottom-back, .tape-bottom-edit, .tape-top-edit {
                        background-color: rgba(255, 255, 255, 0.4);
                        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                        z-index: 10;
                        backdrop-filter: blur(1px);
                        border: 1px solid rgba(255,255,255,0.2);
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: transform 0.2s;
                    }

                    /* 상단 뒤로가기 테이프 */
                    .tape-nav {
                        position: absolute;
                        top: -15px;
                        left: 50%;
                        transform: translateX(-50%) rotate(-1deg);
                        width: 150px;
                        height: 45px;
                    }
                    .tape-nav:hover {
                        transform: translateX(-50%) rotate(0deg) scale(1.05);
                    }

                    /* 상단 수정하기 테이프 */
                    .tape-top-edit {
                        position: absolute;
                        top: -15px;
                        right: 20px;
                        transform: rotate(2deg);
                        width: 120px;
                        height: 45px;
                    }
                    .tape-top-edit:hover {
                        transform: rotate(0deg) scale(1.05);
                    }

                    /* 하단 뒤로가기 테이프 */
                    .tape-bottom-back {
                        position: absolute;
                        bottom: -15px;
                        left: 50%;
                        transform: translateX(-50%) rotate(1deg);
                        width: 150px;
                        height: 45px;
                    }
                    .tape-bottom-back:hover {
                        transform: translateX(-50%) rotate(0deg) scale(1.05);
                    }

                    /* 하단 수정하기 테이프 */
                    .tape-bottom-edit {
                        position: absolute;
                        bottom: -15px;
                        right: 20px;
                        transform: rotate(-2deg);
                        width: 120px;
                        height: 45px;
                    }
                    .tape-bottom-edit:hover {
                        transform: rotate(0deg) scale(1.05);
                    }

                    .tape-arrow {
                        color: #2e7d32; /* 비상구 초록색 */
                        font-size: 28px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-right: 5px; /* 글자와 간격 */
                    }
                    .tape-text {
                        color: #555;
                        font-family: 'Gamja Flower', cursive;
                        font-size: 24px;
                        font-weight: bold;
                        user-select: none;
                    }
                    .tape-text.back {
                        color: #2e7d32; /* 비상구 초록색 */
                    }
                    .tape-text.edit {
                        color: #ef6c00; /* 공사중 주황색 */
                    }

                    .toastui-editor-contents img {
                        max-width: 100%;
                        height: auto;
                        border-radius: 4px;
                        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
                        margin: 10px 0;
                    }
                    
                    /* 제목 및 날짜 스타일 */
                    .detail-title {
                        font-size: 2rem;
                        font-weight: 700;
                        margin-bottom: 10px;
                        color: #333;
                        border-bottom: 2px dashed rgba(0,0,0,0.1);
                        padding-bottom: 15px;
                    }
                    .detail-date {
                        font-size: 1rem;
                        color: #666;
                        text-align: right;
                        margin-bottom: 30px;
                        font-family: 'Gamja Flower', cursive;
                    }
                `}
            </style>

            <div className="post-it-detail" style={cardStyle}>
                {/* 상단 테이프 네비게이션 (뒤로가기 - 비상구 픽토그램) */}
                <div className="tape-nav" onClick={() => navigate(-1)} title="뒤로가기">
                    <span className="tape-arrow">
                        <FaRunning style={{ transform: 'scaleX(-1)' }} /> {/* 달리는 방향 반대로 (왼쪽으로 탈출) */}
                    </span>
                    <span className="tape-text back">뒤로가기</span>
                </div>

                {/* 상단 수정하기 테이프 (관리자용) */}
                {isAdmin && (
                    <div className="tape-top-edit" onClick={() => navigate(`/diary/edit/${id}`)} title="수정하기">
                        <span className="tape-arrow">
                            <FaTools style={{ color: '#ef6c00', fontSize: '22px' }} />
                        </span>
                        <span className="tape-text edit" style={{ marginLeft: '5px' }}>수정하기</span>
                    </div>
                )}

                <h1 className="detail-title">{post.title}</h1>
                <div className="detail-date">
                    {post.created_at?.substring(0, 10)}
                </div>

                <div className="post-content">
                    {post.content && (
                        <Viewer initialValue={post.content} />
                    )}
                </div>

                {/* 댓글 섹션 */}
                <CommentSection postId={id} />

                {/* 하단 테이프 네비게이션 (뒤로가기 - 비상구 픽토그램) */}
                <div className="tape-bottom-back" onClick={() => navigate(-1)} title="뒤로가기">
                    <span className="tape-arrow">
                        <FaRunning style={{ transform: 'scaleX(-1)' }} />
                    </span>
                    <span className="tape-text back">뒤로가기</span>
                </div>

                {/* 하단 수정하기 테이프 (관리자용) */}
                {isAdmin && (
                    <div className="tape-bottom-edit" onClick={() => navigate(`/diary/edit/${id}`)} title="수정하기">
                        <span className="tape-arrow">
                            <FaTools style={{ color: '#ef6c00', fontSize: '22px' }} />
                        </span>
                        <span className="tape-text edit" style={{ marginLeft: '5px' }}>수정하기</span>
                    </div>
                )}
            </div>
        </Container>
    );
};

export default DiaryDetail;
